import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Home,
  TrendingUp,
  Star,
  BookOpen,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Shield,
  Building2,
} from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import userRoles from '../constants/userRoles';
import UserContext from '../hooks/context/UserContext';
import { cn } from '../lib/utils';

const navigationItems = [
  { path: '/home', title: 'Home', icon: Home },
  { path: '/reader', title: 'Tech News', icon: BookOpen },
  { path: '/progress', title: 'Progress', icon: TrendingUp },
  { path: '/reviews', title: 'Reviews', icon: Star },
  { path: '/events', title: 'Events', icon: Calendar },
];

function NavItem({ item, isCollapsed, isActive, onClick }) {
  const Icon = item.icon;

  const content = (
    <button
      type="button"
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-3 h-11 text-sm font-medium transition-colors',
        'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
        isCollapsed && 'justify-center px-2',
        isActive && 'bg-sidebar-accent text-sidebar-foreground',
      )}
      onClick={onClick}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {!isCollapsed && <span className="truncate">{item.title}</span>}
    </button>
  );

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {item.title}
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}

NavItem.propTypes = {
  item: PropTypes.shape({
    path: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
  }).isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

function Sidebar({ isCollapsed, onToggle }) {
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(UserContext);

  const isAdmin = user?.roles?.includes(userRoles.ADMIN);
  const isHead = user?.roles?.includes(userRoles.HEAD);

  const handleNavigation = (path) => {
    history.push(path);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64',
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo Section */}
          <div
            className={cn(
              'flex h-16 items-center border-b border-sidebar-border px-4',
              isCollapsed ? 'justify-center' : 'justify-between',
            )}
          >
            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                  <span className="text-lg font-bold text-primary-foreground">G</span>
                </div>
                <span className="text-lg font-semibold text-sidebar-foreground">Grow</span>
              </div>
            )}
            {isCollapsed && (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <span className="text-lg font-bold text-primary-foreground">G</span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="flex flex-col gap-1">
              {navigationItems.map((item) => (
                <NavItem
                  key={item.path}
                  item={item}
                  isCollapsed={isCollapsed}
                  isActive={location.pathname === item.path}
                  onClick={() => handleNavigation(item.path)}
                />
              ))}
            </nav>

            {/* Admin/Head Section */}
            {(isAdmin || isHead) && (
              <>
                <div className={cn('my-4 border-t border-sidebar-border', isCollapsed && 'mx-2')} />
                {!isCollapsed && (
                  <p className="px-3 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Switch Panel
                  </p>
                )}
                <nav className="flex flex-col gap-1">
                  {isHead && (
                    <NavItem
                      item={{ path: '/head/departments', title: 'Head Panel', icon: Building2 }}
                      isCollapsed={isCollapsed}
                      isActive={location.pathname.startsWith('/head')}
                      onClick={() => handleNavigation('/head/departments')}
                    />
                  )}
                  {isAdmin && (
                    <NavItem
                      item={{ path: '/admin/departments', title: 'Admin Panel', icon: Shield }}
                      isCollapsed={isCollapsed}
                      isActive={location.pathname.startsWith('/admin')}
                      onClick={() => handleNavigation('/admin/departments')}
                    />
                  )}
                </nav>
              </>
            )}
          </ScrollArea>

          {/* Collapse Toggle */}
          <div
            className={cn('border-t border-sidebar-border p-3', !isCollapsed && 'flex justify-end')}
          >
            <button
              type="button"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
              )}
              onClick={onToggle}
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
}

Sidebar.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default Sidebar;
